import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Empty,
    Form,
    Input,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tabs,
    Tag,
    Typography
} from 'antd';
import {
    BarChartOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DownloadOutlined,
    FilterOutlined,
    NodeIndexOutlined,
    FilePdfOutlined,
    ReloadOutlined,
    SearchOutlined,
    StopOutlined
} from '@ant-design/icons';
import { useGetRequests } from '@/shared/api/client/action-flow';
import { useGetAllSubscriptions } from '@/shared/api/client/action-flow-subscription';
import type { ActionFlowRequestDTO, ActionFlowRequestSearch, ActionFlowSubscriptionDTO } from '@/shared/api/model';
import { formatDateTime } from '@/modules/action-flow/shared/utils/formatters';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type ReportView = 'overview' | 'pending' | 'nodes' | 'outcomes' | 'actions';

type WorkflowStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'RETURNED' | 'COMPLETED';

const VIEW_OPTIONS: ReportView[] = ['overview', 'pending', 'nodes', 'outcomes', 'actions'];
const STATUS_OPTIONS: WorkflowStatus[] = ['PENDING', 'APPROVED', 'REJECTED', 'RETURNED', 'COMPLETED'];

const extractRequests = (response: any): ActionFlowRequestDTO[] => {
    if (Array.isArray(response?.data)) {
        return response.data;
    }

    if (Array.isArray(response?.data?.data)) {
        return response.data.data;
    }

    if (Array.isArray(response)) {
        return response;
    }

    return [];
};

const extractSubscriptions = (response: any): ActionFlowSubscriptionDTO[] => {
    if (Array.isArray(response?.data)) {
        return response.data;
    }

    if (Array.isArray(response)) {
        return response;
    }

    return [];
};

const buildKey = (request: ActionFlowRequestDTO, fallback: string) => {
    return request.scopeId || request.recordId || request.logId || request.initLogId || fallback;
};

const escapeCsv = (value: unknown) => {
    const text = String(value ?? '');
    if (text.includes('"') || text.includes(',') || text.includes('\n') || text.includes('\r')) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
};

const downloadFile = (filename: string, blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const normalizeStatus = (request: ActionFlowRequestDTO) => {
    return String(request.approvalStatus || request.status || 'PENDING').toUpperCase();
};

const formatExportDate = (value?: string | null) => {
    return value ? formatDateTime(value) : '-';
};

const getStatusColor = (status?: string) => {
    switch ((status || '').toUpperCase()) {
        case 'APPROVED':
            return 'success';
        case 'REJECTED':
            return 'error';
        case 'RETURNED':
            return 'warning';
        case 'COMPLETED':
            return 'processing';
        case 'PENDING':
        default:
            return 'default';
    }
};

const normalizeView = (value: string | null): ReportView => {
    if (value && VIEW_OPTIONS.includes(value as ReportView)) {
        return value as ReportView;
    }

    return 'overview';
};

export const ActionFlowReportsView: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [form] = Form.useForm();
    const [filters, setFilters] = useState<ActionFlowRequestSearch>({
        numberOfRows: 500,
        sortBy: 'scopeDate',
        orderBy: 'desc'
    });

    const activeView = normalizeView(searchParams.get('view'));
    const subscriptionsQuery = useGetAllSubscriptions({ query: { enabled: true } });

    const buildSearch = (status: WorkflowStatus): ActionFlowRequestSearch => ({
        ...filters,
        status: status as any,
        numberOfRows: filters.numberOfRows || 500,
        sortBy: filters.sortBy || 'scopeDate',
        orderBy: filters.orderBy || 'desc'
    });

    const pendingQuery = useGetRequests({ search: buildSearch('PENDING') }, { query: { enabled: true } });
    const approvedQuery = useGetRequests({ search: buildSearch('APPROVED') }, { query: { enabled: true } });
    const rejectedQuery = useGetRequests({ search: buildSearch('REJECTED') }, { query: { enabled: true } });
    const returnedQuery = useGetRequests({ search: buildSearch('RETURNED') }, { query: { enabled: true } });
    const completedQuery = useGetRequests({ search: buildSearch('COMPLETED') }, { query: { enabled: true } });

    const pendingRequests = useMemo(() => extractRequests(pendingQuery.data), [pendingQuery.data]);
    const approvedRequests = useMemo(() => extractRequests(approvedQuery.data), [approvedQuery.data]);
    const rejectedRequests = useMemo(() => extractRequests(rejectedQuery.data), [rejectedQuery.data]);
    const returnedRequests = useMemo(() => extractRequests(returnedQuery.data), [returnedQuery.data]);
    const completedRequests = useMemo(() => extractRequests(completedQuery.data), [completedQuery.data]);
    const subscriptions = useMemo(() => extractSubscriptions(subscriptionsQuery.data), [subscriptionsQuery.data]);

    const processedRequests = useMemo(
        () => [...approvedRequests, ...rejectedRequests, ...returnedRequests, ...completedRequests],
        [approvedRequests, rejectedRequests, returnedRequests, completedRequests]
    );

    const allRequests = useMemo(() => {
        const map = new Map<string, ActionFlowRequestDTO>();
        [...pendingRequests, ...processedRequests].forEach((request, index) => {
            map.set(buildKey(request, String(index)), request);
        });
        return Array.from(map.values());
    }, [pendingRequests, processedRequests]);

    const loading = [pendingQuery, approvedQuery, rejectedQuery, returnedQuery, completedQuery].some((query) => query.isLoading);
    const fetching = [pendingQuery, approvedQuery, rejectedQuery, returnedQuery, completedQuery].some((query) => query.isFetching);
    const error = pendingQuery.error || approvedQuery.error || rejectedQuery.error || returnedQuery.error || completedQuery.error;

    const summaryRows = useMemo(() => {
        const buckets: Record<WorkflowStatus, ActionFlowRequestDTO[]> = {
            PENDING: pendingRequests,
            APPROVED: approvedRequests,
            REJECTED: rejectedRequests,
            RETURNED: returnedRequests,
            COMPLETED: completedRequests
        };

        return STATUS_OPTIONS.map((status) => ({
            key: status,
            status,
            count: buckets[status].length,
            latestDate: buckets[status][0]?.scopeDate || null
        }));
    }, [pendingRequests, approvedRequests, rejectedRequests, returnedRequests, completedRequests]);

    const pendingByNode = useMemo(() => {
        const counts = new Map<string, { key: string; nodeName: string; domain: string; count: number }>();

        pendingRequests.forEach((request) => {
            const nodeName = request.nodeName || request.currentActionNode || 'Unassigned';
            const key = `${request.domain || 'UNKNOWN'}::${nodeName}`;
            const current = counts.get(key) || {
                key,
                nodeName,
                domain: request.domain || 'UNKNOWN',
                count: 0
            };
            current.count += 1;
            counts.set(key, current);
        });

        return Array.from(counts.values()).sort((a, b) => b.count - a.count);
    }, [pendingRequests]);

    const nodeStatusSummary = useMemo(() => {
        const counts = new Map<string, any>();

        allRequests.forEach((request) => {
            const nodeName = request.nodeName || request.currentActionNode || 'Unassigned';
            const key = `${request.domain || 'UNKNOWN'}::${nodeName}`;
            const current = counts.get(key) || {
                key,
                nodeName,
                domain: request.domain || 'UNKNOWN',
                pending: 0,
                approved: 0,
                rejected: 0,
                returned: 0,
                completed: 0,
                total: 0
            };

            const status = (request.approvalStatus || request.status || 'PENDING').toUpperCase();
            if (status === 'APPROVED') current.approved += 1;
            else if (status === 'REJECTED') current.rejected += 1;
            else if (status === 'RETURNED') current.returned += 1;
            else if (status === 'COMPLETED') current.completed += 1;
            else current.pending += 1;
            current.total += 1;
            counts.set(key, current);
        });

        return Array.from(counts.values()).sort((a, b) => b.total - a.total);
    }, [allRequests]);

    const domainSummary = useMemo(() => {
        const counts = new Map<string, any>();

        allRequests.forEach((request) => {
            const domain = request.domain || 'UNKNOWN';
            const current = counts.get(domain) || {
                key: domain,
                domain,
                total: 0,
                pending: 0,
                processed: 0
            };
            const status = (request.approvalStatus || request.status || 'PENDING').toUpperCase();
            current.total += 1;
            if (status === 'PENDING') {
                current.pending += 1;
            } else {
                current.processed += 1;
            }
            counts.set(domain, current);
        });

        return Array.from(counts.values()).sort((a, b) => b.total - a.total);
    }, [allRequests]);

    const makerSummary = useMemo(() => {
        const counts = new Map<string, any>();

        processedRequests.forEach((request) => {
            const maker = request.createdBy || request.maker || 'Unknown';
            const current = counts.get(maker) || {
                key: maker,
                maker,
                branchName: request.branchName || request.branch || '-',
                approved: 0,
                rejected: 0,
                returned: 0,
                completed: 0,
                total: 0
            };
            const status = (request.approvalStatus || request.status || '').toUpperCase();
            if (status === 'APPROVED') current.approved += 1;
            if (status === 'REJECTED') current.rejected += 1;
            if (status === 'RETURNED') current.returned += 1;
            if (status === 'COMPLETED') current.completed += 1;
            current.total += 1;
            counts.set(maker, current);
        });

        return Array.from(counts.values()).sort((a, b) => b.total - a.total);
    }, [processedRequests]);

    const recentActivity = useMemo(() => {
        return [...allRequests]
            .sort((a, b) => new Date(b.scopeDate || 0).getTime() - new Date(a.scopeDate || 0).getTime())
            .slice(0, 12);
    }, [allRequests]);

    const domainOptions = useMemo(() => {
        return Array.from(new Set(subscriptions.map((item) => item.domain).filter(Boolean) as string[]))
            .sort((a, b) => a.localeCompare(b))
            .map((domain) => ({ label: domain, value: domain }));
    }, [subscriptions]);

    const subscriptionOptions = useMemo(() => {
        return subscriptions
            .filter((item) => !filters.domain || item.domain === filters.domain)
            .map((item) => ({
                label: item.name ? `${item.domain || 'UNKNOWN'} - ${item.name}` : `${item.domain || 'UNKNOWN'} - ${item.subscriptionId || 'N/A'}`,
                value: item.subscriptionId || ''
            }))
            .filter((item, index, array) => item.value && array.findIndex((candidate) => candidate.value === item.value) === index)
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [subscriptions, filters.domain]);

    const currentExport = useMemo(() => {
        if (activeView === 'pending') {
            return {
                title: 'Pending Workflow Report',
                headers: ['Record Name', 'Scope ID', 'Domain', 'Subscription', 'Node / Stage', 'Branch', 'Maker', 'Status', 'Scope Date'],
                rows: pendingRequests.map((record) => [
                    record.recordName || record.subscriptionName || 'Unnamed Request',
                    record.scopeId || record.recordId || '-',
                    record.domain || 'UNKNOWN',
                    record.subscriptionName || record.subscriptionId || '-',
                    record.nodeName || record.currentActionNode || '-',
                    record.branchName || record.branch || '-',
                    record.createdBy || record.maker || '-',
                    normalizeStatus(record),
                    formatExportDate(record.scopeDate)
                ])
            };
        }

        if (activeView === 'nodes') {
            return {
                title: 'Node Stage Report',
                headers: ['Domain', 'Node / Stage', 'Pending', 'Approved', 'Returned', 'Rejected', 'Completed', 'Total'],
                rows: nodeStatusSummary.map((item) => [
                    item.domain,
                    item.nodeName,
                    item.pending,
                    item.approved,
                    item.returned,
                    item.rejected,
                    item.completed,
                    item.total
                ])
            };
        }

        if (activeView === 'outcomes') {
            return {
                title: 'Completed Workflow Report',
                headers: ['Record Name', 'Scope ID', 'Domain', 'Subscription', 'Node / Stage', 'Branch', 'Maker', 'Status', 'Scope Date'],
                rows: processedRequests.map((record) => [
                    record.recordName || record.subscriptionName || 'Unnamed Request',
                    record.scopeId || record.recordId || '-',
                    record.domain || 'UNKNOWN',
                    record.subscriptionName || record.subscriptionId || '-',
                    record.nodeName || record.currentActionNode || '-',
                    record.branchName || record.branch || '-',
                    record.createdBy || record.maker || '-',
                    normalizeStatus(record),
                    formatExportDate(record.scopeDate)
                ])
            };
        }

        if (activeView === 'actions') {
            return {
                title: 'Action Summary Report',
                headers: ['Maker', 'Branch', 'Approved', 'Returned', 'Rejected', 'Completed', 'Total Actions'],
                rows: makerSummary.map((item) => [
                    item.maker,
                    item.branchName,
                    item.approved,
                    item.returned,
                    item.rejected,
                    item.completed,
                    item.total
                ])
            };
        }

        return {
            title: 'Workflow Overview Report',
            headers: ['Record Name', 'Scope ID', 'Domain', 'Subscription', 'Node / Stage', 'Branch', 'Maker', 'Status', 'Scope Date'],
            rows: allRequests.map((record) => [
                record.recordName || record.subscriptionName || 'Unnamed Request',
                record.scopeId || record.recordId || '-',
                record.domain || 'UNKNOWN',
                record.subscriptionName || record.subscriptionId || '-',
                record.nodeName || record.currentActionNode || '-',
                record.branchName || record.branch || '-',
                record.createdBy || record.maker || '-',
                normalizeStatus(record),
                formatExportDate(record.scopeDate)
            ])
        };
    }, [activeView, allRequests, makerSummary, nodeStatusSummary, pendingRequests, processedRequests]);

    const requestColumns = [
        {
            title: 'Record Name',
            dataIndex: 'recordName',
            key: 'recordName',
            render: (value: string, record: ActionFlowRequestDTO) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{value || record.subscriptionName || 'Unnamed Request'}</Text>
                    <Text type="secondary">{record.scopeId || record.recordId}</Text>
                </Space>
            )
        },
        {
            title: 'Domain',
            dataIndex: 'domain',
            key: 'domain',
            render: (value: string) => <Tag color="blue">{value || 'UNKNOWN'}</Tag>
        },
        {
            title: 'Node / Stage',
            key: 'nodeName',
            render: (_: unknown, record: ActionFlowRequestDTO) => record.nodeName || record.currentActionNode || '-'
        },
        {
            title: 'Branch',
            key: 'branchName',
            render: (_: unknown, record: ActionFlowRequestDTO) => record.branchName || record.branch || '-'
        },
        {
            title: 'Maker',
            key: 'createdBy',
            render: (_: unknown, record: ActionFlowRequestDTO) => record.createdBy || record.maker || '-'
        },
        {
            title: 'Status',
            key: 'approvalStatus',
            render: (_: unknown, record: ActionFlowRequestDTO) => {
                const status = (record.approvalStatus || record.status || 'PENDING').toUpperCase();
                return <Tag color={getStatusColor(status)}>{status}</Tag>;
            }
        },
        {
            title: 'Scope Date',
            dataIndex: 'scopeDate',
            key: 'scopeDate',
            render: (value: string) => value ? formatDateTime(value) : '-'
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: unknown, record: ActionFlowRequestDTO) => (
                <Link to={`/requests/approval/${record.scopeId}`}>
                    View Workflow
                </Link>
            )
        }
    ];

    const handleFilter = (values: any) => {
        setFilters({
            q: values.q || undefined,
            domain: values.domain || undefined,
            subscriptionId: values.subscriptionId || undefined,
            from: values.dateRange?.[0]?.format('YYYY-MM-DD'),
            to: values.dateRange?.[1]?.format('YYYY-MM-DD'),
            numberOfRows: 500,
            sortBy: 'scopeDate',
            orderBy: 'desc'
        });
    };

    const handleReset = () => {
        form.resetFields();
        setFilters({
            numberOfRows: 500,
            sortBy: 'scopeDate',
            orderBy: 'desc'
        });
    };

    const handleRefresh = async () => {
        await Promise.all([
            pendingQuery.refetch(),
            approvedQuery.refetch(),
            rejectedQuery.refetch(),
            returnedQuery.refetch(),
            completedQuery.refetch(),
            subscriptionsQuery.refetch()
        ]);
    };

    const handleExportCsv = () => {
        const csv = [currentExport.headers, ...currentExport.rows]
            .map((row) => row.map((cell) => escapeCsv(cell)).join(','))
            .join('\n');
        downloadFile(`${currentExport.title.toLowerCase().replace(/\s+/g, '-')}.csv`, new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    };

    const handleExportPdf = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.setFontSize(16);
        doc.text(currentExport.title, pageWidth / 2, 14, { align: 'center' });
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 21, { align: 'center' });
        autoTable(doc, {
            startY: 28,
            head: [currentExport.headers],
            body: currentExport.rows,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [24, 144, 255] },
            margin: { left: 8, right: 8 }
        });
        doc.save(`${currentExport.title.toLowerCase().replace(/\s+/g, '-')}.pdf`);
    };

    const setActiveTab = (nextView: string) => {
        const next = new URLSearchParams(searchParams);
        next.set('view', nextView);
        setSearchParams(next);
    };

    const renderTable = (dataSource: ActionFlowRequestDTO[]) => {
        if (!dataSource.length && !loading) {
            return <Empty description="No workflow records found for the current filters" />;
        }

        return (
            <Table
                columns={requestColumns}
                dataSource={dataSource}
                rowKey={(record, index) => buildKey(record, String(index))}
                loading={loading}
                pagination={{ pageSize: 10, showSizeChanger: true }}
            />
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Space direction="vertical" size={0}>
                    <Title level={2} style={{ margin: 0 }}>Action Flow Reports</Title>
                    <Text type="secondary">
                        Workflow reporting for pending queues, node distribution, completed outcomes, rejected or returned actions, and summary views.
                    </Text>
                </Space>
                <Space>
                    <Button icon={<DownloadOutlined />} onClick={handleExportCsv} disabled={!currentExport.rows.length}>
                        Export CSV
                    </Button>
                    <Button icon={<FilePdfOutlined />} onClick={handleExportPdf} disabled={!currentExport.rows.length}>
                        Export PDF
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={fetching || subscriptionsQuery.isFetching}>
                        Refresh
                    </Button>
                </Space>
            </div>

            {error ? (
                <Alert
                    type="error"
                    showIcon
                    message="Failed to load workflow reports"
                    description={error instanceof Error ? error.message : 'Unknown error'}
                />
            ) : null}

            <Card>
                <Form form={form} layout="vertical" onFinish={handleFilter}>
                    <Row gutter={16}>
                        <Col xs={24} md={6}>
                            <Form.Item name="q" label="Search">
                                <Input prefix={<SearchOutlined />} placeholder="Search record, scope or maker" allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="domain" label="Domain">
                                <Select
                                    allowClear
                                    placeholder="All domains"
                                    loading={subscriptionsQuery.isLoading}
                                    options={domainOptions}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="subscriptionId" label="Subscription / Purpose">
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="All subscriptions"
                                    loading={subscriptionsQuery.isLoading}
                                    options={subscriptionOptions}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="dateRange" label="Date Range">
                                <RangePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Space>
                        <Button onClick={handleReset}>Reset</Button>
                        <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>Apply Filters</Button>
                    </Space>
                </Form>
            </Card>

            <Row gutter={16}>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic title="Total Workflows" value={allRequests.length} prefix={<BarChartOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic title="Pending" value={pendingRequests.length} prefix={<ClockCircleOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic title="Approved" value={approvedRequests.length} prefix={<CheckCircleOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic title="Returned" value={returnedRequests.length} prefix={<NodeIndexOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic title="Rejected" value={rejectedRequests.length} prefix={<StopOutlined />} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic title="Completed" value={completedRequests.length} prefix={<CheckCircleOutlined />} />
                    </Card>
                </Col>
            </Row>

            <Card>
                <Tabs
                    activeKey={activeView}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'overview',
                            label: 'Overview',
                            children: (
                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Table
                                        dataSource={summaryRows}
                                        rowKey="key"
                                        pagination={false}
                                        columns={[
                                            { title: 'Status', dataIndex: 'status', key: 'status', render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag> },
                                            { title: 'Count', dataIndex: 'count', key: 'count' },
                                            { title: 'Latest Activity', dataIndex: 'latestDate', key: 'latestDate', render: (value: string | null) => value ? formatDateTime(value) : '-' }
                                        ]}
                                    />
                                    <Table
                                        dataSource={domainSummary}
                                        rowKey="key"
                                        pagination={false}
                                        columns={[
                                            { title: 'Domain', dataIndex: 'domain', key: 'domain', render: (value: string) => <Tag color="blue">{value}</Tag> },
                                            { title: 'Total', dataIndex: 'total', key: 'total' },
                                            { title: 'Pending', dataIndex: 'pending', key: 'pending' },
                                            { title: 'Processed', dataIndex: 'processed', key: 'processed' }
                                        ]}
                                    />
                                    {renderTable(recentActivity)}
                                </Space>
                            )
                        },
                        {
                            key: 'pending',
                            label: 'Pending Queue',
                            children: (
                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Table
                                        dataSource={pendingByNode}
                                        rowKey="key"
                                        pagination={false}
                                        columns={[
                                            { title: 'Domain', dataIndex: 'domain', key: 'domain', render: (value: string) => <Tag color="blue">{value}</Tag> },
                                            { title: 'Pending Node / Stage', dataIndex: 'nodeName', key: 'nodeName' },
                                            { title: 'Pending Count', dataIndex: 'count', key: 'count' }
                                        ]}
                                    />
                                    {renderTable(pendingRequests)}
                                </Space>
                            )
                        },
                        {
                            key: 'nodes',
                            label: 'Node / Stage Breakdown',
                            children: (
                                <Table
                                    dataSource={nodeStatusSummary}
                                    rowKey="key"
                                    pagination={{ pageSize: 10, showSizeChanger: true }}
                                    columns={[
                                        { title: 'Domain', dataIndex: 'domain', key: 'domain', render: (value: string) => <Tag color="blue">{value}</Tag> },
                                        { title: 'Node / Stage', dataIndex: 'nodeName', key: 'nodeName' },
                                        { title: 'Pending', dataIndex: 'pending', key: 'pending' },
                                        { title: 'Approved', dataIndex: 'approved', key: 'approved' },
                                        { title: 'Returned', dataIndex: 'returned', key: 'returned' },
                                        { title: 'Rejected', dataIndex: 'rejected', key: 'rejected' },
                                        { title: 'Completed', dataIndex: 'completed', key: 'completed' },
                                        { title: 'Total', dataIndex: 'total', key: 'total' }
                                    ]}
                                />
                            )
                        },
                        {
                            key: 'outcomes',
                            label: 'Outcomes',
                            children: renderTable(processedRequests)
                        },
                        {
                            key: 'actions',
                            label: 'Action Summary',
                            children: (
                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Table
                                        dataSource={summaryRows.filter((row) => row.status !== 'PENDING')}
                                        rowKey="key"
                                        pagination={false}
                                        columns={[
                                            { title: 'Action / Outcome', dataIndex: 'status', key: 'status', render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag> },
                                            { title: 'Count', dataIndex: 'count', key: 'count' },
                                            { title: 'Latest Activity', dataIndex: 'latestDate', key: 'latestDate', render: (value: string | null) => value ? formatDateTime(value) : '-' }
                                        ]}
                                    />
                                    <Table
                                        dataSource={makerSummary}
                                        rowKey="key"
                                        pagination={{ pageSize: 10, showSizeChanger: true }}
                                        columns={[
                                            { title: 'Maker', dataIndex: 'maker', key: 'maker' },
                                            { title: 'Branch', dataIndex: 'branchName', key: 'branchName' },
                                            { title: 'Approved', dataIndex: 'approved', key: 'approved' },
                                            { title: 'Returned', dataIndex: 'returned', key: 'returned' },
                                            { title: 'Rejected', dataIndex: 'rejected', key: 'rejected' },
                                            { title: 'Completed', dataIndex: 'completed', key: 'completed' },
                                            { title: 'Total Actions', dataIndex: 'total', key: 'total' }
                                        ]}
                                    />
                                </Space>
                            )
                        }
                    ]}
                />
            </Card>
        </div>
    );
};

export default ActionFlowReportsView;
